/**
 * Utilidades compartidas para validación y formateo de entidades del sistema de mantenimiento
 *
 * Proporciona funciones reutilizables para trabajar con equipos, mantenimientos
 * y categorías en el frontend, siguiendo el principio DRY.
 */

/**
 * Valida los datos de un formulario de entidad según el tipo
 * @param {Object} data - Datos del formulario
 * @param {string} type - Tipo de entidad: 'equipo', 'mantenimiento', 'categoria'
 * @returns {Object} { isValid: boolean, errors: Object }
 */
export const validateEntityForm = (data, type) => {
  const errors = {};

  // Validaciones específicas para equipos
  if (type === 'equipo') {
    if (!data.codigo || data.codigo.trim() === '') {
      errors.codigo = 'El código es obligatorio';
    } else if (data.codigo.trim().length < 2) {
      errors.codigo = 'El código debe tener al menos 2 caracteres';
    } else if (data.codigo.trim().length > 50) {
      errors.codigo = 'El código no puede exceder 50 caracteres';
    }

    if (!data.marca || data.marca.trim() === '') {
      errors.marca = 'La marca es obligatoria';
    } else if (data.marca.trim().length > 100) {
      errors.marca = 'La marca no puede exceder 100 caracteres';
    }

    if (!data.modelo || data.modelo.trim() === '') {
      errors.modelo = 'El modelo es obligatorio';
    } else if (data.modelo.trim().length > 100) {
      errors.modelo = 'El modelo no puede exceder 100 caracteres';
    }

    if (!data.año_equipo) {
      errors.año_equipo = 'El año del equipo es obligatorio';
    } else {
      const año = parseInt(data.año_equipo);
      const currentYear = new Date().getFullYear();
      if (isNaN(año) || año < 1900 || año > currentYear + 5) {
        errors.año_equipo = `El año debe estar entre 1900 y ${currentYear + 5}`;
      }
    }

    if (!data.categoria_equipo_id) {
      errors.categoria_equipo_id = 'La categoría es obligatoria';
    }

    if (data.estado && !['operativo', 'en_mantenimiento', 'fuera_de_servicio', 'dado_de_baja'].includes(data.estado)) {
      errors.estado = 'Estado inválido';
    }
  }

  // Validaciones específicas para órdenes de trabajo
  if (type === 'ordenTrabajo') {
    if (!data.equipo_id) {
      errors.equipo_id = 'El equipo es obligatorio';
    }

    if (!data.tipo_mantenimiento_id) {
      errors.tipo_mantenimiento_id = 'El tipo de mantenimiento es obligatorio';
    }

    if (!data.fecha_programada) {
      errors.fecha_programada = 'La fecha programada es obligatoria';
    }

    if (!data.descripcion || data.descripcion.trim() === '') {
      errors.descripcion = 'La descripción es obligatoria';
    } else if (data.descripcion.trim().length < 5) {
      errors.descripcion = 'La descripción debe tener al menos 5 caracteres';
    }

    if (data.estado && !['programado', 'en_progreso', 'completado', 'cancelado'].includes(data.estado)) {
      errors.estado = 'Estado inválido';
    }

    if (data.prioridad && !['baja', 'media', 'alta', 'critica'].includes(data.prioridad)) {
      errors.prioridad = 'Prioridad inválida';
    }

  }

  // Validaciones específicas para categorías
  if (type === 'categoria') {
    if (!data.nombre || data.nombre.trim() === '') {
      errors.nombre = 'El nombre es obligatorio';
    } else if (data.nombre.trim().length < 2) {
      errors.nombre = 'El nombre debe tener al menos 2 caracteres';
    } else if (data.nombre.trim().length > 100) {
      errors.nombre = 'El nombre no puede exceder 100 caracteres';
    }

  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Formatea los datos de una entidad antes de enviarlos al backend
 * @param {Object} data - Datos crudos del formulario
 * @param {string} type - Tipo de entidad: 'equipo', 'mantenimiento', 'categoria'
 * @returns {Object} Datos formateados
 */
export const formatEntityData = (data, type) => {
  const formatted = {};

  if (type === 'equipo') {
    formatted.codigo = data.codigo?.trim() || '';
    formatted.marca = data.marca?.trim() || '';
    formatted.modelo = data.modelo?.trim() || '';
    formatted.año_equipo = parseInt(data.año_equipo);
    formatted.categoria_equipo_id = parseInt(data.categoria_equipo_id);
    if (data.numero_serie) formatted.numero_serie = data.numero_serie.trim();
    if (data.estado) formatted.estado = data.estado;
    if (data.notas) formatted.notas = data.notas.trim();
  }

  if (type === 'ordenTrabajo') {
    if (data.equipo_id) formatted.equipo_id = parseInt(data.equipo_id);
    if (data.tipo_mantenimiento_id) formatted.tipo_mantenimiento_id = parseInt(data.tipo_mantenimiento_id);
    if (data.fecha_programada) formatted.fecha_programada = data.fecha_programada;
    if (data.fecha_inicio) formatted.fecha_inicio = data.fecha_inicio;
    if (data.fecha_fin) formatted.fecha_fin = data.fecha_fin;
    formatted.descripcion = data.descripcion?.trim() || '';
    if (data.estado) formatted.estado = data.estado;
    if (data.prioridad) formatted.prioridad = data.prioridad;
  }

  if (type === 'categoria') {
    formatted.nombre = data.nombre?.trim() || '';
    if (data.descripcion) formatted.descripcion = data.descripcion.trim();
  }

  return formatted;
};

/**
 * Obtiene la configuración de columnas para la tabla según el tipo de entidad
 * @param {string} type - Tipo de entidad: 'equipo', 'mantenimiento', 'categoria'
 * @returns {Array} Configuración de columnas
 */
export const getEntityColumns = (type) => {
  if (type === 'equipo') {
    return [
      { key: 'id', label: 'ID', sortable: true, align: 'center', tooltip: 'Identificador único del equipo' },
      { key: 'codigo', label: 'Código', sortable: true, align: 'left', tooltip: 'Código del equipo', highlightable: true },
      { key: 'marca', label: 'Marca', sortable: true, align: 'left', tooltip: 'Marca del equipo' },
      { key: 'modelo', label: 'Modelo', sortable: true, align: 'left', tooltip: 'Modelo del equipo' },
      { key: 'año_equipo', label: 'Año', sortable: true, align: 'center', tooltip: 'Año del equipo' },
      { key: 'categoria_nombre', label: 'Categoría', sortable: true, align: 'left', tooltip: 'Categoría del equipo' },
      { key: 'estado', label: 'Estado', sortable: true, align: 'center', tooltip: 'Estado actual del equipo' },
      { key: 'updated_at', label: 'Última modificación', sortable: true, align: 'center', tooltip: 'Fecha de última actualización' }
    ];
  }

  if (type === 'ordenTrabajo') {
    return [
      { key: 'id', label: 'ID', sortable: true, align: 'center', tooltip: 'Identificador único de la OT' },
      { key: 'equipo_nombre', label: 'Equipo', sortable: true, align: 'left', tooltip: 'Equipo a mantener', highlightable: true },
      { key: 'tipo_nombre', label: 'Tipo', sortable: true, align: 'left', tooltip: 'Tipo de mantenimiento' },
      { key: 'fecha_ejecucion', label: 'Fecha ejecución', sortable: true, align: 'center', tooltip: 'Fecha de ejecución del mantenimiento' },
      { key: 'estado', label: 'Estado', sortable: true, align: 'center', tooltip: 'Estado del mantenimiento' },
      { key: 'prioridad', label: 'Prioridad', sortable: true, align: 'center', tooltip: 'Prioridad del mantenimiento' },
      { key: 'updated_at', label: 'Última modificación', sortable: true, align: 'center', tooltip: 'Fecha de última actualización' }
    ];
  }

  if (type === 'categoria') {
    return [
      { key: 'id', label: 'ID', sortable: true, align: 'center', tooltip: 'Identificador único de la categoría' },
      { key: 'nombre', label: 'Nombre', sortable: true, align: 'left', tooltip: 'Nombre de la categoría', highlightable: true },
      { key: 'descripcion', label: 'Descripción', sortable: false, align: 'left', tooltip: 'Descripción de la categoría' },
      { key: 'updated_at', label: 'Última modificación', sortable: true, align: 'center', tooltip: 'Fecha de última actualización' }
    ];
  }

  return [];
};

/**
 * Obtiene el mensaje de estado vacío según el tipo de entidad
 * @param {string} type - Tipo de entidad: 'equipo', 'mantenimiento', 'categoria'
 * @returns {string} Mensaje para mostrar cuando no hay datos
 */
export const getEntityEmptyMessage = (type) => {
  if (type === 'equipo') {
    return 'No se encontraron equipos con los filtros aplicados';
  }
  if (type === 'ordenTrabajo') {
    return 'No se encontraron órdenes de trabajo con los filtros aplicados';
  }
  if (type === 'categoria') {
    return 'No se encontraron categorías con los filtros aplicados';
  }
  return 'No se encontraron resultados con los filtros aplicados';
};

/**
 * Formatea el valor de una celda según la columna y el tipo de entidad
 * @param {string} columnKey - Clave de la columna
 * @param {any} value - Valor de la celda
 * @param {Object} row - Fila completa de datos
 * @returns {string|JSX} Valor formateado
 */
export const formatCellValue = (columnKey, value, row) => {
  switch (columnKey) {
    case 'activo':
      return value ? 'Activo' : 'Inactivo';

    case 'estado':
      if (!value) return '-';
      const estadoLabels = {
        'operativo': 'Operativo',
        'en_mantenimiento': 'En Mantenimiento',
        'fuera_de_servicio': 'Fuera de Servicio',
        'dado_de_baja': 'Dado de Baja',
        'programado': 'Programado',
        'en_progreso': 'En Progreso',
        'completado': 'Completado',
        'cancelado': 'Cancelado'
      };
      const estadoClass = {
        'operativo': 'badge-success',
        'en_mantenimiento': 'badge-warning',
        'fuera_de_servicio': 'badge-danger',
        'dado_de_baja': 'badge-secondary',
        'programado': 'badge-info',
        'en_progreso': 'badge-warning',
        'completado': 'badge-success',
        'cancelado': 'badge-secondary'
      };
      const label = estadoLabels[value] || value;
      const badgeClass = estadoClass[value] || 'badge-secondary';
      return `<span class="badge ${badgeClass}">${label}</span>`;

    case 'prioridad':
      if (!value) return '-';
      const prioridadLabels = {
        'baja': 'Baja',
        'media': 'Media',
        'alta': 'Alta',
        'critica': 'Crítica'
      };
      const prioridadClass = {
        'baja': 'badge-secondary',
        'media': 'badge-info',
        'alta': 'badge-warning',
        'critica': 'badge-danger'
      };
      const prioLabel = prioridadLabels[value] || value;
      const prioBadge = prioridadClass[value] || 'badge-secondary';
      return `<span class="badge ${prioBadge}">${prioLabel}</span>`;

    case 'categoria_nombre':
    case 'equipo_nombre':
    case 'tipo_nombre':
      return value || 'Sin asignar';

    case 'fecha_programada':
    case 'fecha_ejecucion':
    case 'fecha_inicio':
    case 'fecha_fin':
    case 'updated_at':
    case 'created_at':
      if (!value) return '-';
      // Formatear fecha a DD/MM/YYYY
      const date = new Date(value);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;

    case 'año_equipo':
      return value || '-';

    default:
      return value || '-';
  }
};

